import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography,
  Chip,
  Button,
  Divider,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PeopleIcon from '@material-ui/icons/People';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { getOverlapVector, getThesisIntegrityData } from './thesisIntegrityUtils';
import { getPredicateSections } from './thesisSignalPredicateUtils';
import { buildOutcomeBadges, shouldShowOutcomeRow } from './thesisOutcomeUtils';
import ScoreMethodologyTooltip from './ScoreMethodologyTooltip';
import ActionBadge from './ActionBadge';
import ActionCard from './ActionCard';
import { SERVER_NAME } from './config';

const useStyles = makeStyles((theme) => ({
  panel: {
    background: 'radial-gradient(circle at top left, rgba(77,121,255,0.28), rgba(10,14,30,0.98))',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.45)',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  title: {
    fontSize: 18,
    fontWeight: 900,
    letterSpacing: -0.2,
    color: '#e5e7ff',
    marginBottom: 6,
  },
  meta: {
    fontSize: 12,
    color: '#cfd3ff',
  },
  addrMono: {
    fontSize: 12,
    fontFamily: 'monospace',
    color: '#cfd8ff',
    wordBreak: 'break-all',
  },
  section: {
    marginTop: 12,
  },
  block: {
    padding: 14,
    borderRadius: 12,
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    border: '1px solid rgba(158, 247, 197, 0.18)',
    color: '#e8f5ff',
    fontSize: 13,
    lineHeight: 1.65,
    whiteSpace: 'pre-wrap',
  },
  signalsGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 12,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  signalBox: {
    borderRadius: 12,
    padding: 12,
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    border: '1px solid rgba(255,255,255,0.10)',
  },
  signalTitle: {
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  list: {
    margin: 0,
    paddingLeft: 18,
  },
  listItem: {
    marginBottom: 8,
    color: '#e8f5ff',
    fontSize: 13,
    lineHeight: 1.55,
  },
  predicateList: {
    marginTop: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  predicateRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    color: '#e8f5ff',
    fontSize: 12,
    lineHeight: 1.5,
  },
  predicateDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginTop: 4,
    flex: '0 0 auto',
    boxShadow: '0 0 0 2px rgba(255,255,255,0.08)',
  },
  chartWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  tradeBox: {
    borderRadius: 12,
    padding: 12,
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    border: '1px solid rgba(255,255,255,0.10)',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 10,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  input: {
    '& .MuiInputBase-root': {
      color: '#e5e7ff',
      background: 'rgba(5, 8, 20, 0.62)',
      borderRadius: 10,
      border: '1px solid rgba(255,255,255,0.10)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiInputLabel-root': {
      color: '#9ea4d8',
    },
  },
  buttonRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  primaryButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    padding: '8px 12px',
    '&:hover': { backgroundColor: '#3e61cc' },
  },
  ghostButton: {
    textTransform: 'none',
    fontWeight: 700,
    color: '#cfd3ff',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 10,
    padding: '8px 12px',
  },
  barRow: {
    display: 'grid',
    gridTemplateColumns: '84px minmax(0, 1fr) 48px',
    gap: 10,
    alignItems: 'center',
    marginTop: 8,
  },
  barTrack: {
    height: 10,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, rgba(90,167,255,0.88), rgba(158,247,197,0.92))',
  },
  equationToggle: {
    textTransform: 'none',
    fontWeight: 700,
    color: '#cfd3ff',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 10,
    padding: '4px 10px',
    minWidth: 0,
  },
}));

function shortenAddress(addr) {
  const a = String(addr || '');
  if (!a) return '';
  if (a.length <= 12) return a;
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

function shortenAddressesInText(text) {
  const s = String(text || '');
  if (!s) return '';
  return s.replace(/0x[0-9a-fA-F]{40}/g, (m) => `${m.slice(0, 6)}...${m.slice(-4)}`);
}

function formatUsd(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x <= 0) return '—';
  if (x >= 1e9) return `$${(x / 1e9).toFixed(2)}B`;
  if (x >= 1e6) return `$${(x / 1e6).toFixed(2)}M`;
  if (x >= 1e3) return `$${(x / 1e3).toFixed(2)}K`;
  if (x >= 1) return `$${x.toFixed(4)}`;
  return `$${x.toPrecision(3)}`;
}

function formatRelative(ts) {
  if (!ts) return '';
  const d = new Date(String(ts));
  if (isNaN(d.getTime())) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

function convictionPointColor(reason) {
  if (!reason) return '#5aa7ff';
  const r = String(reason).toLowerCase();
  if (r.includes('machine-checkable entry')) return '#9ef7c5';
  if (r.includes('machine-checkable invalidation')) return '#ff7a7a';
  if (r.includes('confirms') || r.includes('surge') || r.includes('formation')) return '#9ef7c5';
  if (r.includes('against') || r.includes('counter') || r.includes('reverse')) return '#ff7a7a';
  return '#9ea4d8';
}

function ConvictionChart({ history }) {
  const [hovered, setHovered] = useState(null);
  const points = Array.isArray(history) ? history : [];
  if (points.length < 2) return null;

  const width = 340;
  const height = 84;
  const padding = 10;

  const xs = points.map((_, i) => padding + (i / (points.length - 1)) * (width - 2 * padding));
  const ys = points.map((p) => {
    const c = Math.max(0, Math.min(1, Number(p && p.conviction != null ? p.conviction : 0)));
    return height - padding - c * (height - 2 * padding);
  });

  const poly = xs.map((x, i) => `${x},${ys[i]}`).join(' ');

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <svg
        width={width} height={height}
        style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 8, display: 'block' }}
        onMouseLeave={() => setHovered(null)}
      >
        {[0.25, 0.5, 0.75].map((v) => {
          const y = height - padding - v * (height - 2 * padding);
          return <line key={v} x1={padding} y1={y} x2={width - padding} y2={y} stroke="rgba(255,255,255,0.07)" strokeWidth={1} />;
        })}
        <polyline points={poly} fill="none" stroke="#5aa7ff" strokeWidth={2} />
        {points.map((p, i) => (
          <circle
            key={i} cx={xs[i]} cy={ys[i]}
            r={hovered && hovered.idx === i ? 6 : 4}
            fill={convictionPointColor(p && p.reason)}
            style={{ cursor: 'default' }}
            onMouseEnter={() => setHovered({ idx: i, x: xs[i], y: ys[i] })}
          />
        ))}
      </svg>
      {hovered != null && (() => {
        const p = points[hovered.idx];
        const score = (Number(p && p.conviction) || 0).toFixed(2);
        const ts = formatRelative(p && p.ts);
        const reason = p && p.reason ? String(p.reason) : null;
        const tipLeft = Math.min(hovered.x + 10, width - 164);
        const tipTop = hovered.y < 40 ? hovered.y + 14 : hovered.y - 58;
        return (
          <div style={{
            position: 'absolute', left: tipLeft, top: tipTop,
            background: 'rgba(20,24,40,0.95)', border: '1px solid rgba(100,120,200,0.4)',
            borderRadius: 6, padding: '5px 9px', pointerEvents: 'none',
            fontSize: 11, color: '#d0d4f0', lineHeight: 1.5, zIndex: 10, maxWidth: 160,
          }}>
            <div style={{ fontWeight: 700, color: '#fff' }}>{score}</div>
            <div style={{ color: '#888' }}>{ts}</div>
            {reason && <div style={{ color: '#b0b8e0', marginTop: 2 }}>{reason}</div>}
          </div>
        );
      })()}
    </div>
  );
}

export default function ThesisDetail({ thesis, annotation, isAuthenticated, isPremiumPlus, onSaveAnnotation, onOpenStory, openLogin, hideContributingStories = false, showGroupToggle = false, isGrouped = false, onToggleGroup = null }) {
  const classes = useStyles();

  const showLoginDialog = useCallback((e) => {
    try {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    } catch (err) {
    }

    if (typeof openLogin === 'function') {
      try { openLogin(); } catch (err2) {}
      return;
    }
    try {
      if (typeof window !== 'undefined' && typeof window.globalShowLogin === 'function') {
        window.globalShowLogin(true);
      }
      if (typeof window !== 'undefined' && typeof window.setGlobalShowLogin === 'function') {
        window.setGlobalShowLogin(true);
      }
      if (typeof document !== 'undefined') {
        document.dispatchEvent(new Event('force-login-dialog-render', { bubbles: true, cancelable: true }));
      }
    } catch (e) {
    }
  }, [openLogin]);

  const [userStatus, setUserStatus] = useState('none');
  const [userConvictionRaw, setUserConvictionRaw] = useState('');
  const [notes, setNotes] = useState('');
  const [entryPrice, setEntryPrice] = useState('');
  const [exitPrice, setExitPrice] = useState('');
  const [positionSize, setPositionSize] = useState('');
  const [saveBusy, setSaveBusy] = useState(false);

  const [tradeActionsExpanded, setTradeActionsExpanded] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [showEquation, setShowEquation] = useState(false);
  const [thesisActions, setThesisActions] = useState([]);
  const [thesisActionsExpanded, setThesisActionsExpanded] = useState(false);
  const [actionOutcomes, setActionOutcomes] = useState({});

  useEffect(() => {
    const a = annotation || null;
    setUserStatus(a && a.userStatus ? String(a.userStatus) : 'none');
    setUserConvictionRaw(a && a.userConviction != null && a.userConviction !== '' ? String(a.userConviction) : '');
    setNotes(a && a.notes ? String(a.notes) : '');
    setEntryPrice(a && a.entryPrice != null ? String(a.entryPrice) : '');
    setExitPrice(a && a.exitPrice != null ? String(a.exitPrice) : '');
    setPositionSize(a && a.positionSize ? String(a.positionSize) : '');
  }, [annotation, thesis && thesis.id]);

  const thesisId = thesis && thesis.id ? String(thesis.id) : '';
  useEffect(() => {
    if (!thesisId) return;
    fetch(`${SERVER_NAME}/thesis/${thesisId}/actions`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        const acts = data.actions || [];
        setThesisActions(acts);
        // Fetch outcomes for completed actions
        const closedIds = acts.filter((a) => !['open', 'evaluating'].includes(a.status)).map((a) => a.action_id);
        if (!closedIds.length) return;
        Promise.all(
          closedIds.map((id) =>
            fetch(`${SERVER_NAME}/actions/${id}/outcome`)
              .then((r) => (r.ok ? r.json() : null))
              .then((d) => (d && d.outcome ? [String(id), d.outcome] : null))
              .catch(() => null)
          )
        ).then((results) => {
          const map = {};
          for (const r of results) { if (r) map[r[0]] = r[1]; }
          setActionOutcomes(map);
        });
      })
      .catch(() => {});
  }, [thesisId]);

  const addr = thesis && thesis.entity_address ? String(thesis.entity_address) : '';
  const sym = thesis && thesis.entity_symbol ? String(thesis.entity_symbol) : '';
  const name = thesis && thesis.entity_name ? String(thesis.entity_name) : '';
  const direction = thesis && thesis.direction ? String(thesis.direction).toUpperCase() : '';

  const dirChip = useMemo(() => {
    if (direction === 'LONG') return { bg: '#2e7d32', label: 'LONG' };
    if (direction === 'SHORT') return { bg: '#c62828', label: 'SHORT' };
    if (direction === 'AVOID') return { bg: '#616161', label: 'AVOID' };
    return { bg: 'rgba(255,255,255,0.18)', label: direction || '—' };
  }, [direction]);

  const conviction = Number(thesis && thesis.conviction != null ? thesis.conviction : 0);

  const uniswapLink = useMemo(() => {
    if (!addr) return '';
    return `https://app.uniswap.org/#/swap?outputCurrency=${addr}&chain=ethereum`;
  }, [addr]);

  const oneInchLink = useMemo(() => {
    if (!addr) return '';
    return `https://app.1inch.io/#/1/simple/swap/ETH/${addr}`;
  }, [addr]);

  const handleCopy = useCallback(async () => {
    try {
      if (!addr) return;
      await navigator.clipboard.writeText(addr);
    } catch (e) {
    }
  }, [addr]);

  const parseMaybeNumber = (s) => {
    const t = String(s || '').trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  const handleSave = useCallback(async () => {
    if (!thesis || !thesis.id) return;
    if (!isAuthenticated) return;
    setSaveBusy(true);
    try {
      const payload = {
        userStatus,
        notes,
      };

      const uc = parseMaybeNumber(userConvictionRaw);
      payload.userConviction = uc === null ? null : Math.max(0, Math.min(1, uc));

      if (isPremiumPlus) {
        const ep = parseMaybeNumber(entryPrice);
        const xp = parseMaybeNumber(exitPrice);
        if (ep !== null || entryPrice === '') payload.entryPrice = ep;
        if (xp !== null || exitPrice === '') payload.exitPrice = xp;
        payload.positionSize = String(positionSize || '');
      }

      await onSaveAnnotation(thesis.id, payload);
    } finally {
      setSaveBusy(false);
    }
  }, [thesis, isAuthenticated, userStatus, notes, userConvictionRaw, isPremiumPlus, entryPrice, exitPrice, positionSize, onSaveAnnotation]);

  if (!thesis) {
    return (
      <div className={classes.panel}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ea4d8' }}>
          Select a thesis to view details.
        </div>
      </div>
    );
  }

  const riskFactors = Array.isArray(thesis.risk_factors) ? thesis.risk_factors : [];
  const storyIds = Array.isArray(thesis.contributing_story_ids) ? thesis.contributing_story_ids : [];
  const history = Array.isArray(thesis.conviction_history) ? thesis.conviction_history : [];
  const overlapVector = getOverlapVector(thesis);
  const integrityData = getThesisIntegrityData(thesis);
  const candidateRuleSnapshot = Array.isArray(thesis.candidate_rule_weight_snapshot) ? thesis.candidate_rule_weight_snapshot : [];
  const candidateScoreBreakdown = thesis && thesis.candidate_score_breakdown && typeof thesis.candidate_score_breakdown === 'object'
    ? thesis.candidate_score_breakdown
    : {};
  const predicateSections = getPredicateSections(thesis);
  const outcomeBadges = buildOutcomeBadges(thesis);
  const showOutcomeRow = shouldShowOutcomeRow(thesis, 24);

  const isExpired = thesis.status === 'expired';

  return (
    <div className={classes.panel}>
      {isExpired && (
        <div style={{ background: 'rgba(255,200,50,0.10)', border: '1px solid rgba(255,200,50,0.35)', borderRadius: 8, padding: '6px 12px', marginBottom: 10, color: '#ffe082', fontSize: 12 }}>
          This thesis expired — shown for historical context. Conviction score reflects state at expiry.
        </div>
      )}
      <div className={classes.headerRow}>
        <div style={{ minWidth: 260, flex: '1 1 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Chip size="small" label={dirChip.label} style={{ backgroundColor: dirChip.bg, color: '#fff', fontWeight: 900 }} />
            <Typography className={classes.title}>{sym ? `${sym}${name ? ` (${name})` : ''}` : (name || shortenAddress(addr))}</Typography>
          </div>

          <Typography className={classes.meta}>
            {`Conviction: ${Number.isFinite(conviction) ? conviction.toFixed(2) : '—'} · ${thesis.time_horizon || '—'} horizon · Status: ${thesis.status || '—'}${thesis.state ? ` · State: ${thesis.state}` : ''}`}
          </Typography>

          <Typography className={classes.meta} style={{ marginTop: 4 }}>
            {`Created: ${formatRelative(thesis.ts_created)} · Updated: ${formatRelative(thesis.ts_updated)}`}
          </Typography>

          <Typography className={classes.meta} style={{ marginTop: 4 }}>
            {`Price at creation: ${formatUsd(thesis.price_at_creation)} · Market cap: ${formatUsd(thesis.market_cap_at_creation)}`}
          </Typography>
          {showOutcomeRow ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              <Typography className={classes.meta}>Outcome:</Typography>
              {outcomeBadges.map((item) => {
                const color = item.status === 'matched'
                  ? '#9ef7c5'
                  : (item.status === 'did-not-match' ? '#ff9a9a' : '#cfd3ff');
                const border = item.status === 'matched'
                  ? 'rgba(158,247,197,0.35)'
                  : (item.status === 'did-not-match' ? 'rgba(255,122,122,0.35)' : 'rgba(255,255,255,0.12)');
                const returnText = Number.isFinite(item.fwdReturn) ? `${item.fwdReturn >= 0 ? '+' : ''}${(item.fwdReturn * 100).toFixed(1)}%` : 'pending';
                return (
                  <span
                    key={item.horizon}
                    style={{
                      background: 'rgba(5, 8, 20, 0.72)',
                      border: `1px solid ${border}`,
                      borderRadius: 999,
                      padding: '2px 8px',
                      fontSize: 11,
                      color,
                    }}
                  >
                    {`${item.horizon}: ${returnText} · ${item.status}`}
                  </span>
                );
              })}
            </div>
          ) : null}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography className={classes.addrMono}>{shortenAddress(addr)}</Typography>
          <Tooltip title="Copy address">
            <IconButton size="small" onClick={handleCopy} style={{ color: '#cfd8ff', padding: 3 }}>
              <FileCopyIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {showGroupToggle ? (
            <Tooltip title={isGrouped ? 'Remove from group evidence' : 'Add to group evidence'}>
              <IconButton
                size="small"
                onClick={(e) => {
                  try {
                    if (e && typeof e.preventDefault === 'function') e.preventDefault();
                    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                  } catch (err) {
                  }
                  if (typeof onToggleGroup === 'function') onToggleGroup(e);
                }}
                style={{ color: isGrouped ? '#9ef7c5' : '#9ea4d8', padding: 4 }}
              >
                <PeopleIcon style={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.block}>{thesis.thesis_text ? shortenAddressesInText(thesis.thesis_text) : ''}</div>
      </div>

      <div className={classes.section}>
        <div className={classes.signalsGrid}>
          <div className={classes.signalBox} style={{ borderColor: 'rgba(46, 125, 50, 0.55)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div className={classes.signalTitle} style={{ color: '#9ef7c5', marginBottom: 0 }}>Entry Signal</div>
              {!predicateSections.hasMachineCheckableEntry ? (
                <Chip size="small" label="Not Machine-Checkable" style={{ background: 'rgba(255,224,130,0.14)', color: '#ffe082', height: 20 }} />
              ) : null}
            </div>
            <div style={{ color: '#e8f5ff', fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{thesis.entry_signal ? shortenAddressesInText(thesis.entry_signal) : '—'}</div>
            {!!predicateSections.entry.length && (
              <div className={classes.predicateList}>
                {predicateSections.entry.map((predicate, idx) => (
                  <Tooltip key={`entry-${idx}`} title={predicate.tooltip}>
                    <div className={classes.predicateRow}>
                      <span className={classes.predicateDot} style={{ backgroundColor: predicate.dotColor }} />
                      <span>{predicate.text}</span>
                    </div>
                  </Tooltip>
                ))}
              </div>
            )}
          </div>
          <div className={classes.signalBox} style={{ borderColor: 'rgba(198, 40, 40, 0.55)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div className={classes.signalTitle} style={{ color: '#ff7a7a', marginBottom: 0 }}>Invalidation Signal</div>
              {(!predicateSections.hasMachineCheckableInvalidation || predicateSections.hasUnevaluatedPredicates) ? (
                <Chip
                  size="small"
                  label={!predicateSections.hasMachineCheckableInvalidation ? 'Not Machine-Checkable' : 'Partially Machine-Checkable'}
                  style={{ background: 'rgba(255,224,130,0.14)', color: '#ffe082', height: 20 }}
                />
              ) : null}
            </div>
            <div style={{ color: '#e8f5ff', fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{thesis.invalidation_signal ? shortenAddressesInText(thesis.invalidation_signal) : '—'}</div>
            {!!predicateSections.invalidation.length && (
              <div className={classes.predicateList}>
                {predicateSections.invalidation.map((predicate, idx) => (
                  <Tooltip key={`invalidation-${idx}`} title={predicate.tooltip}>
                    <div className={classes.predicateRow}>
                      <span className={classes.predicateDot} style={{ backgroundColor: predicate.dotColor }} />
                      <span>{predicate.text}</span>
                    </div>
                  </Tooltip>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={classes.section}>
        <Typography style={{ color: '#e5e7ff', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 12 }}>
          Convergence
        </Typography>
        <Typography className={classes.meta} style={{ marginTop: 6 }}>
          {`O_t = Σ λ_i O_i · total ${Number(thesis.o_total || 0).toFixed(2)} · ${Number(thesis.n_supporting || storyIds.length || 0)} supporting stories`}
        </Typography>
        <div style={{ marginTop: 8 }}>
          {overlapVector.map((item) => (
            <Tooltip key={item.key} title={item.tooltip}>
              <div className={classes.barRow}>
                <Typography className={classes.meta} style={{ color: '#e8f5ff' }}>{item.label}</Typography>
                <div className={classes.barTrack}>
                  <div className={classes.barFill} style={{ width: `${item.value * 100}%` }} />
                </div>
                <Typography className={classes.meta} style={{ textAlign: 'right' }}>{item.value.toFixed(2)}</Typography>
              </div>
            </Tooltip>
          ))}
        </div>
        <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: 'rgba(5, 8, 20, 0.72)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <Typography className={classes.meta}>
            {`Higher overlap means the thesis is being supported by the same addresses, tokens, contracts, venues, timing cluster, and motif across multiple story types. Diversity: ${Number(thesis.diversity || 0).toFixed(2)}.`}
          </Typography>
        </div>
      </div>

      {/* Trading Levels */}
      {(thesis.entry_zone_min > 0 || thesis.target_1 > 0 || thesis.invalidation_price > 0) && (
        <div className={classes.section}>
          <Typography style={{ color: '#e5e7ff', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 12, marginBottom: 8 }}>
            Trading Levels {thesis.entry_zone_min > 0 && thesis.estimated_slippage_bps >= 9999 ? '' : '(model-estimated)'}
          </Typography>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 12 }}>
            {thesis.entry_zone_min > 0 && (
              <span style={{ background: 'rgba(77,121,255,0.18)', border: '1px solid rgba(77,121,255,0.4)', borderRadius: 6, padding: '3px 8px', color: '#9ef7c5' }}>
                Entry: ${Number(thesis.entry_zone_min).toFixed(6)}–${Number(thesis.entry_zone_max).toFixed(6)}
              </span>
            )}
            {thesis.target_1 > 0 && (
              <span style={{ background: 'rgba(0,200,83,0.12)', border: '1px solid rgba(0,200,83,0.35)', borderRadius: 6, padding: '3px 8px', color: '#9ef7c5' }}>
                T1: ${Number(thesis.target_1).toFixed(6)}
              </span>
            )}
            {thesis.target_2 > 0 && (
              <span style={{ background: 'rgba(0,200,83,0.12)', border: '1px solid rgba(0,200,83,0.35)', borderRadius: 6, padding: '3px 8px', color: '#b2dfdb' }}>
                T2: ${Number(thesis.target_2).toFixed(6)}
              </span>
            )}
            {thesis.target_3 > 0 && (
              <span style={{ background: 'rgba(0,200,83,0.12)', border: '1px solid rgba(0,200,83,0.35)', borderRadius: 6, padding: '3px 8px', color: '#80cbc4' }}>
                T3: ${Number(thesis.target_3).toFixed(6)}
              </span>
            )}
            {thesis.invalidation_price > 0 && (
              <span style={{ background: 'rgba(198,40,40,0.12)', border: '1px solid rgba(198,40,40,0.4)', borderRadius: 6, padding: '3px 8px', color: '#ff7a7a' }}>
                Stop: ${Number(thesis.invalidation_price).toFixed(6)}
              </span>
            )}
            {thesis.estimated_slippage_bps > 0 && thesis.estimated_slippage_bps < 9999 && (
              <span style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '3px 8px', color: '#9ea4d8' }}>
                ~{thesis.estimated_slippage_bps} bps slippage on $10K
              </span>
            )}
          </div>
        </div>
      )}

      <div className={classes.section}>
        <Divider style={{ background: 'rgba(255,255,255,0.08)' }} />
      </div>

      <div className={classes.section}>
        <Typography style={{ color: '#e5e7ff', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 12 }}>
          Risk Factors
        </Typography>
        {/* Security scan indicator */}
        {thesis.fraud_risk != null && (
          <div style={{ marginTop: 6, marginBottom: 4, fontSize: 12 }}>
            {thesis.fraud_risk < 0
              ? <span style={{ color: '#9ea4d8' }}>🔍 Security scan pending</span>
              : thesis.fraud_risk >= 80
              ? <span style={{ color: '#ff7a7a' }}>🚨 Fraud risk: {Math.round(thesis.fraud_risk)}/100 (high)</span>
              : thesis.fraud_risk >= 40
              ? <span style={{ color: '#ffe082' }}>⚠️ Fraud risk: {Math.round(thesis.fraud_risk)}/100 (elevated)</span>
              : <span style={{ color: '#9ef7c5' }}>✓ Fraud risk: {Math.round(thesis.fraud_risk)}/100 (low)</span>
            }
          </div>
        )}
        {!riskFactors.length ? (
          <Typography className={classes.meta} style={{ marginTop: 6 }}>—</Typography>
        ) : (
          <ul className={classes.list} style={{ marginTop: 6 }}>
            {riskFactors.map((r, idx) => (
              <li key={idx} className={classes.listItem}>{`⚠️ ${shortenAddressesInText(String(r))}`}</li>
            ))}
          </ul>
        )}
      </div>

      {!hideContributingStories ? (
        <div className={classes.section}>
          <Typography style={{ color: '#e5e7ff', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 12 }}>
            Contributing Stories
          </Typography>
          {!storyIds.length ? (
            <Typography className={classes.meta} style={{ marginTop: 6 }}>—</Typography>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              {storyIds.slice(0, 30).map((sid) => (
                <div key={String(sid)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <Typography className={classes.addrMono} style={{ fontFamily: 'monospace' }}>{String(sid)}</Typography>
                  <Button
                    size="small"
                    className={classes.ghostButton}
                    endIcon={<OpenInNewIcon />}
                    onClick={() => onOpenStory && onOpenStory(String(sid))}
                  >
                    View in Stories
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      <div className={classes.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Typography style={{ color: '#e5e7ff', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 12 }}>
              {showEquation ? 'Thesis Integrity Equation' : 'Conviction History'}
            </Typography>
            <ScoreMethodologyTooltip
              glyph="sqrt"
              ariaLabel="How this conviction was measured"
              title="Conviction Under OTA"
              equation="C^Theta_t = 0.25·Q_S + 0.25·O + 0.15·D + 0.15·H + 0.10·X − 0.10·R"
              lines={[
                'Use the equation toggle to inspect the live thesis-integrity decomposition.',
                'The sparkline annotates machine-checkable entry and invalidation events when they fire.',
              ]}
              onActivate={(e) => {
                if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                setShowEquation((value) => !value);
              }}
              buttonStyle={{ padding: 2, color: '#9ea4d8' }}
            />
          </div>
          <Button
            size="small"
            className={classes.equationToggle}
            onClick={() => setShowEquation((value) => !value)}
          >
            {showEquation ? 'Show sparkline' : 'Show the equation'}
          </Button>
        </div>
        {showEquation ? (
          <div style={{ marginTop: 10 }}>
            <div
              style={{ overflowX: 'auto', padding: '6px 0' }}
              dangerouslySetInnerHTML={{
                __html: katex.renderToString(
                  'C^{\\Theta}_{t} = 0.25\\,Q^{S}_{t} + 0.25\\,O_t + 0.15\\,D_t + 0.15\\,H_t + 0.10\\,X_t - 0.10\\,R^{\\Theta}_{t}',
                  { displayMode: true, throwOnError: false }
                ),
              }}
            />
            {integrityData ? (
              <div style={{ marginTop: 8 }}>
                {integrityData.components.map((item) => (
                  <div key={item.key} className={classes.barRow}>
                    <Typography className={classes.meta} style={{ color: '#e8f5ff' }}>
                      {`${item.sign === '-' ? '-' : '+'} ${item.label}`}
                    </Typography>
                    <div style={{ color: '#cfd8ff', fontSize: 12 }}>
                      {`${item.weight.toFixed(2)} × ${item.value.toFixed(2)} = ${item.contribution.toFixed(2)}`}
                    </div>
                    <Typography className={classes.meta} style={{ textAlign: 'right' }}>
                      {item.contribution >= 0 ? `+${item.contribution.toFixed(2)}` : item.contribution.toFixed(2)}
                    </Typography>
                  </div>
                ))}
                <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <Chip size="small" label={`Base ${integrityData.c_thesis_base.toFixed(2)}`} style={{ background: 'rgba(77,121,255,0.16)', color: '#cfd8ff' }} />
                  <Chip size="small" label={`f_Theta ${integrityData.f_theta.toFixed(2)}`} style={{ background: 'rgba(158,247,197,0.14)', color: '#9ef7c5' }} />
                  <Chip size="small" label={`Enhanced ${integrityData.c_thesis_enhanced.toFixed(2)}`} style={{ background: 'rgba(255,255,255,0.10)', color: '#ffffff' }} />
                </div>
                <Typography className={classes.meta} style={{ marginTop: 8 }}>
                  {`Velocity: ${Number(thesis.conviction_velocity || 0).toFixed(3)} / hr · Acceleration: ${Number(thesis.conviction_acceleration || 0).toFixed(3)} / hr² · τ_up ${Number(thesis.tau_up || 0).toFixed(2)} · τ_down ${Number(thesis.tau_down || 0).toFixed(2)}`}
                </Typography>
                <div style={{ marginTop: 12 }}>
                  <Typography className={classes.meta} style={{ color: '#e8f5ff', fontWeight: 700 }}>
                    {`Candidate convergence score ${Number(thesis.candidate_convergence_score || 0).toFixed(2)} under rule weight set v${Number(thesis.candidate_rule_weight_version || 1)}`}
                  </Typography>
                  {candidateRuleSnapshot.length ? candidateRuleSnapshot.map((item) => (
                    <div key={`${item.rule_name}-${item.version}`} className={classes.barRow}>
                      <Typography className={classes.meta} style={{ color: '#e8f5ff' }}>
                        {item.rule_name}
                      </Typography>
                      <div style={{ color: '#cfd8ff', fontSize: 12 }}>
                        {`v${Number(item.version || 1)} · weight ${Number(item.weight || 0).toFixed(2)}`}
                      </div>
                      <Typography className={classes.meta} style={{ textAlign: 'right' }}>
                        {Number(item.is_pinned || 0) ? 'Pinned' : 'Live'}
                      </Typography>
                    </div>
                  )) : (
                    <Typography className={classes.meta} style={{ marginTop: 6 }}>No stored rule-weight snapshot.</Typography>
                  )}
                  <Typography className={classes.meta} style={{ marginTop: 8 }}>
                    {`Rule subtotal ${Number(candidateScoreBreakdown.total_rule_weight || 0).toFixed(2)} · unique-type bonus ${Number(candidateScoreBreakdown.unique_story_type_bonus || 0).toFixed(2)} · avg-story bonus ${Number(candidateScoreBreakdown.avg_story_score_bonus || 0).toFixed(2)} · breaking bonus ${Number(candidateScoreBreakdown.breaking_bonus || 0).toFixed(2)}`}
                  </Typography>
                </div>
              </div>
            ) : (
              <Typography className={classes.meta} style={{ marginTop: 8 }}>—</Typography>
            )}
          </div>
        ) : (
          <div className={classes.chartWrap} style={{ marginTop: 8 }}>
            <ConvictionChart history={history} />
            {!history.length ? <Typography className={classes.meta}>—</Typography> : null}
          </div>
        )}
      </div>

      <div className={classes.section}>
        <Divider style={{ background: 'rgba(255,255,255,0.08)' }} />
      </div>

      {thesisActions.length > 0 ? (
        <div className={classes.section}>
          <div className={classes.section}>
            <div style={{ background: 'rgba(255,255,255,0.06)', height: 1, width: '100%' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Typography style={{ color: '#e5e7ff', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 12 }}>
              Decision Layer
            </Typography>
            {thesisActions.filter((a) => a.status === 'open').length > 0 && (
              <ActionBadge actionType={thesisActions.find((a) => a.status === 'open').action_type} />
            )}
            <IconButton
              size="small"
              title={thesisActionsExpanded ? 'Collapse' : 'Expand'}
              onClick={(e) => {
                if (e && typeof e.preventDefault === 'function') e.preventDefault();
                if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                setThesisActionsExpanded((v) => !v);
              }}
              style={{ padding: 2, color: '#cfd3ff' }}
            >
              {thesisActionsExpanded ? <ExpandMoreIcon style={{ fontSize: 18 }} /> : <ChevronRightIcon style={{ fontSize: 18 }} />}
            </IconButton>
          </div>
          {thesisActionsExpanded ? (
            <div style={{ marginTop: 4 }}>
              {thesisActions.map((action) => (
                <ActionCard
                  key={action.action_id}
                  action={action}
                  outcome={actionOutcomes[String(action.action_id)] || null}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={classes.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Typography style={{ color: '#e5e7ff', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 12 }}>
              Trade Actions
            </Typography>
            <IconButton
              size="small"
              title={tradeActionsExpanded ? 'Collapse' : 'Expand'}
              onClick={(e) => {
                if (e && typeof e.preventDefault === 'function') e.preventDefault();
                if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                setTradeActionsExpanded((v) => !v);
              }}
              style={{ padding: 2, color: '#cfd3ff' }}
            >
              {tradeActionsExpanded ? <ExpandMoreIcon style={{ fontSize: 18 }} /> : <ChevronRightIcon style={{ fontSize: 18 }} />}
            </IconButton>
          </div>
        </div>

        {tradeActionsExpanded ? (
          !isAuthenticated ? (
            <Typography className={classes.meta} style={{ marginTop: 8 }}>
              <a
                href="#login"
                style={{ color: '#9ef7c5', fontWeight: 900, cursor: 'pointer', textDecoration: 'underline' }}
                onClick={showLoginDialog}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    showLoginDialog(e);
                  }
                }}
              >
                Sign in
              </a>
              {' '}to unlock trade tools.
            </Typography>
          ) : !isPremiumPlus ? (
            <Typography className={classes.meta} style={{ marginTop: 8 }}>
              Upgrade to Premium to unlock trade tools.
            </Typography>
          ) : (
            <div className={classes.tradeBox} style={{ marginTop: 8 }}>
              <div className={classes.buttonRow}>
                <Button
                  className={classes.primaryButton}
                  endIcon={<OpenInNewIcon />}
                  onClick={() => { if (uniswapLink) window.open(uniswapLink, '_blank'); }}
                >
                  Open on Uniswap
                </Button>
                <Button
                  className={classes.ghostButton}
                  endIcon={<OpenInNewIcon />}
                  onClick={() => { if (oneInchLink) window.open(oneInchLink, '_blank'); }}
                >
                  Open on 1inch
                </Button>
                <Button
                  className={classes.ghostButton}
                  startIcon={<FileCopyIcon />}
                  onClick={handleCopy}
                >
                  Copy Address
                </Button>
              </div>

              <div style={{ marginTop: 12 }}>
                <Typography className={classes.meta} style={{ marginBottom: 6 }}>Track Position</Typography>
                <div className={classes.formGrid}>
                  <TextField
                    className={classes.input}
                    label="Entry Price"
                    variant="outlined"
                    size="small"
                    value={entryPrice}
                    onChange={(e) => setEntryPrice(e.target.value)}
                  />
                  <TextField
                    className={classes.input}
                    label="Position Size"
                    variant="outlined"
                    size="small"
                    value={positionSize}
                    onChange={(e) => setPositionSize(e.target.value)}
                  />
                  <TextField
                    className={classes.input}
                    label="Exit Price"
                    variant="outlined"
                    size="small"
                    value={exitPrice}
                    onChange={(e) => setExitPrice(e.target.value)}
                  />
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Button className={classes.primaryButton} disabled={saveBusy} onClick={handleSave}>
                      {saveBusy ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )
        ) : null}
      </div>

      <div className={classes.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Typography style={{ color: '#e5e7ff', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 12 }}>
              Your Notes
            </Typography>
            <IconButton
              size="small"
              title={notesExpanded ? 'Collapse' : 'Expand'}
              onClick={(e) => {
                if (e && typeof e.preventDefault === 'function') e.preventDefault();
                if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                setNotesExpanded((v) => !v);
              }}
              style={{ padding: 2, color: '#cfd3ff' }}
            >
              {notesExpanded ? <ExpandMoreIcon style={{ fontSize: 18 }} /> : <ChevronRightIcon style={{ fontSize: 18 }} />}
            </IconButton>
          </div>
        </div>

        {notesExpanded ? (
          !isAuthenticated ? (
            <Typography className={classes.meta} style={{ marginTop: 8 }}>
              <a
                href="#login"
                style={{ color: '#9ef7c5', fontWeight: 900, cursor: 'pointer', textDecoration: 'underline' }}
                onClick={showLoginDialog}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    showLoginDialog(e);
                  }
                }}
              >
                Sign in
              </a>
              {' '}to take notes.
            </Typography>
          ) : (
            <div className={classes.tradeBox} style={{ marginTop: 8 }}>
              <div className={classes.formGrid}>
                <TextField
                  className={classes.input}
                  select
                  label="Status"
                  variant="outlined"
                  size="small"
                  value={userStatus}
                  onChange={(e) => setUserStatus(e.target.value)}
                >
                  {['none', 'watching', 'acted_on', 'passed'].map((v) => (
                    <MenuItem key={v} value={v}>{v}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  className={classes.input}
                  label="Override Conviction (0-1)"
                  variant="outlined"
                  size="small"
                  value={userConvictionRaw}
                  onChange={(e) => setUserConvictionRaw(e.target.value)}
                />
              </div>

              <div style={{ marginTop: 10 }}>
                <TextField
                  className={classes.input}
                  label="Notes"
                  variant="outlined"
                  size="small"
                  multiline
                  minRows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                />
              </div>

              <div style={{ marginTop: 10 }}>
                <Button className={classes.primaryButton} disabled={saveBusy} onClick={handleSave}>
                  {saveBusy ? 'Saving…' : (isPremiumPlus ? 'Save' : 'Save Notes')}
                </Button>
              </div>
            </div>
          )
        ) : null}
      </div>
    </div>
  );
}
